import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useAppContext } from '../../context/AppContext';
import { processEntitiesForMap } from '../../services/apiService';
import { ChevronUp, ChevronDown } from 'lucide-react';

// Fix for default markers
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Constants for performance optimization
const CHUNK_SIZE = 1000; // Process GeoJSON in chunks of 1000 features
const DEBOUNCE_DELAY = 300; // Debounce layer updates

// Scale-dependent rendering configuration
const ZOOM_LEVELS = {
  OVERVIEW: { min: 0, max: 16 }, // Show major components only
  DETAIL: { min: 17, max: 19 }, // Show detailed components
  FULL_DETAIL: { min: 20, max: 25 } // Show all components
};

// Layer visibility by zoom level
const LAYER_ZOOM_CONFIG = {
  // Overview level (zoomed out) - show major infrastructure
  'inverter_base': { minZoom: 0, maxZoom: 25, priority: 1 },
  'inverter_boxed': { minZoom: 17, maxZoom: 25, priority: 1 },
  'ug_conduit_34.5kv': { minZoom: 0, maxZoom: 25, priority: 2 },
  
  // Detail level (medium zoom) - show combiners and infrastructure
  'combiner_outlines': { minZoom: 17, maxZoom: 25, priority: 3 },
  'combiner_boxes': { minZoom: 17, maxZoom: 25, priority: 3 },
  'ug_conduit': { minZoom: 16, maxZoom: 25, priority: 2 },
  
  // Full detail level (zoomed in) - show all components
  'rack_outlines': { minZoom: 19, maxZoom: 25, priority: 4 },
  
  // Hidden layers
  'site_polygon': { minZoom: 99, maxZoom: 99, priority: 99 }, // Hidden
  'site_layout_line': { minZoom: 99, maxZoom: 99, priority: 99 } // Hidden
};

const MapTab: React.FC = () => {
  const mapRef = useRef<HTMLDivElement>(null);
  const leafletMap = useRef<L.Map | null>(null);
  const geoJsonLayers = useRef<Map<string, L.GeoJSON>>(new Map());
  const tileLayer = useRef<L.TileLayer | null>(null);
  const siteMarker = useRef<L.Marker | null>(null);
  const { state, setSelectedComponents, entities, loading, error, selectedTenant, sites } = useAppContext();
  const [fallbackGeoJSON, setFallbackGeoJSON] = useState<any>(null);
  const [usingFallback, setUsingFallback] = useState(false);
  const [loadingGeoJSON, setLoadingGeoJSON] = useState(false);
  const [processedChunks, setProcessedChunks] = useState(0);
  const [totalChunks, setTotalChunks] = useState(0);
  const [currentZoom, setCurrentZoom] = useState(15);
  const layerUpdateTimeout = useRef<NodeJS.Timeout | null>(null);
  
  // Convert entities to GeoJSON for map display
  const siteGeoJSON = useMemo(() => {
    if (!entities.length) return null;
    return processEntitiesForMap(entities);
  }, [entities]);

  // Get current site with location data
  const currentSiteWithLocation = useMemo(() => {
    const currentSite = sites.find(site => site.site_id === state.selectedSite);

    // Check if current site has location data (could be string "lat,lng" format or GeoJSON)
    if (currentSite && currentSite.location) {
      let processedSite = { ...currentSite };

      // If location is a string in "lat,lng" format, convert to GeoJSON
      if (typeof currentSite.location === 'string' && currentSite.location.includes(',')) {
        const locationString = currentSite.location as string;
        const [lat, lng] = locationString.split(',').map((coord: string) => parseFloat(coord.trim()));
        if (!isNaN(lat) && !isNaN(lng)) {
          processedSite.location = {
            type: 'Point',
            coordinates: [lng, lat] // GeoJSON format: [longitude, latitude]
          };
        } else {
          return null;
        }
      }

      return processedSite;
    }

    return null;
  }, [sites, state.selectedSite]);

  // Optimized chunked GeoJSON loading
  const loadGeoJSONInChunks = useCallback(async (geoJsonData: any) => {
    if (!geoJsonData?.features) return;

    setLoadingGeoJSON(true);
    const features = geoJsonData.features;
    const chunks = [];
    
    // Split features into chunks
    for (let i = 0; i < features.length; i += CHUNK_SIZE) {
      chunks.push(features.slice(i, i + CHUNK_SIZE));
    }
    
    setTotalChunks(chunks.length);
    setProcessedChunks(0);

    // Process chunks with delays to prevent blocking
    for (let i = 0; i < chunks.length; i++) {
      await new Promise(resolve => setTimeout(resolve, 10)); // Small delay between chunks
      setProcessedChunks(i + 1);
    }

    setLoadingGeoJSON(false);
    return { type: 'FeatureCollection', features };
  }, []);
  
  // Load fallback GeoJSON when no entities are available or when database fails
  useEffect(() => {
    // Trigger fallback if: no entities AND not loading AND no fallback yet
    // OR if there's an error from database AND no fallback yet
    // OR if we're not loading and have no data at all
    if ((!entities.length && !loading && !fallbackGeoJSON && !loadingGeoJSON) ||
        (error && !fallbackGeoJSON && !loadingGeoJSON)) {
      setLoadingGeoJSON(true);
      fetch('/enriched_geojson_20250903_151714.geojson')
        .then(res => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return res.json();
        })
        .then(async (data) => {
          await loadGeoJSONInChunks(data);
          setFallbackGeoJSON(data);
          setUsingFallback(true);
          setLoadingGeoJSON(false);
        })
        .catch(err => {
          setLoadingGeoJSON(false);
          // Still set empty fallback to prevent infinite retries
          setFallbackGeoJSON({ type: 'FeatureCollection', features: [] });
          setUsingFallback(true);
        });
    } else if (entities.length > 0 && !error) {
      // Only disable fallback if we have entities AND no error
      setUsingFallback(false);
    }
  }, [entities, loading, error, fallbackGeoJSON, loadingGeoJSON, loadGeoJSONInChunks]);

  // Define layer configurations based on entity types or fallback data
  const layerConfigs = useMemo(() => {
    // If using fallback GeoJSON
    if (usingFallback && fallbackGeoJSON) {
      // Priority layers to show (in rendering order - first = bottom, last = top)
      const priorityTypes = [
        'site_polygon',
        'ug_conduit',
        'ug_conduit_34.5kv',
        'site_layout_line',
        'rack_outlines',
        'inverter_boxed',
        'inverter_base',
        'combiner_boxes', 
        'combiner_outlines'  // This will render on top
      ];
      
      const fallbackTypes = Array.from(new Set(
        fallbackGeoJSON.features
          .map((f: any) => f.properties?.type || 'unknown')
          .filter((type: string) => type !== 'LineString' && type !== 'name') // Filter out geometry types
      )) as string[];
      
      // Sort by priority, keeping priority types in exact order (for layer z-ordering)
      const sortedTypes = priorityTypes.filter(type => fallbackTypes.includes(type))
        .concat(fallbackTypes.filter(type => !priorityTypes.includes(type)).sort());
      
      
      return sortedTypes.map((type, index) => {
        const zoomConfig = LAYER_ZOOM_CONFIG[type as keyof typeof LAYER_ZOOM_CONFIG];
        
        // Determine if layer should be visible at current zoom
        const isVisibleAtZoom = zoomConfig ? 
          currentZoom >= zoomConfig.minZoom && currentZoom <= zoomConfig.maxZoom :
          true; // Show by default if not configured
        
        // Assign specific colors for key layer types
        let color = '#888888'; // Default gray
        if (type === 'rack_outlines') {
          color = '#8B5CF6'; // Purple for rack outlines
        } else if (type === 'combiner_boxes' || type === 'combiner_outlines') {
          color = '#10B981'; // Green for combiner boxes
        } else if (type.includes('inverter')) {
          color = '#ef5350'; // Red for inverters
        } else if (type.includes('ug_conduit')) {
          color = '#F59E0B'; // Orange for conduits
        } else {
          // Use array colors for other types
          const colors = ['#3B82F6', '#06B6D4', '#84CC16', '#EC4899'];
          color = colors[index % colors.length];
        }
        
        // Make combiners, inverters, and their outlines/bases selectable
        const isSelectable = type.includes('combiner') || type.includes('inverter') || 
                            type === 'combiner_outlines' || type === 'inverter_base';
        
        return {
          id: type.toLowerCase().replace(/[^a-z0-9_]/g, '_'),
          name: type.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()),
          type: type,
          color: color,
          visible: isVisibleAtZoom,
          selectable: isSelectable,
          description: `${type.replace(/_/g, ' ')} layer`,
          minZoom: zoomConfig?.minZoom || 0,
          maxZoom: zoomConfig?.maxZoom || 25,
          priority: zoomConfig?.priority || 5
        };
      }).sort((a, b) => a.priority - b.priority); // Sort by priority for proper rendering order
    }
    
    // Original logic for database entities
    if (!entities.length) return [];
    
    // Get unique entity types from the data
    const entityTypes = Array.from(new Set(
      entities
        .filter(e => e.location)
        .map(e => e.pvcollada_component_type || e.entity_type_name || 'unknown')
    ));
    
    // Create layer configs for each entity type
    return entityTypes.map((type, index) => {
      const colors = ['#3B82F6', '#10B981', '#F59E0B', '#ef5350', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'];
      const isSelectable = type.toLowerCase().includes('combiner') || 
                          type.toLowerCase().includes('inverter') || 
                          type.toLowerCase().includes('module');
      
      return {
        id: type.toLowerCase().replace(/[^a-z0-9]/g, '_'),
        name: type,
        type: type,
        color: colors[index % colors.length],
        visible: true,
        selectable: isSelectable,
        description: `${type} components`,
        minZoom: 0,
        maxZoom: 25,
        priority: 5
      };
    });
  }, [entities, usingFallback, fallbackGeoJSON, currentZoom]);
  
  // Initialize visible layers - need to do this after layerConfigs is computed
  const [visibleLayers, setVisibleLayers] = useState<Set<string>>(new Set());
  const [isLegendCollapsed, setIsLegendCollapsed] = useState(false);
  
  // Update visible layers when layerConfigs changes
  useEffect(() => {
    if (layerConfigs.length > 0) {
      setVisibleLayers(new Set(layerConfigs.filter(config => config.visible).map(config => config.id)));
    }
  }, [layerConfigs]);

  // Add site marker and zoom when site location is available
  const updateSiteMarker = useCallback(() => {
    if (!leafletMap.current) return;

    // Remove existing site marker
    if (siteMarker.current) {
      leafletMap.current.removeLayer(siteMarker.current);
      siteMarker.current = null;
    }

    // Add new site marker if we have location data
    if (currentSiteWithLocation?.location && typeof currentSiteWithLocation.location === 'object' && currentSiteWithLocation.location.coordinates) {
      const [lng, lat] = currentSiteWithLocation.location.coordinates;

      // Use explicit icon for site marker
      const siteIcon = L.icon({
        iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
        shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
        iconSize: [25, 41],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34],
        shadowSize: [41, 41]
      });

      siteMarker.current = L.marker([lat, lng], { icon: siteIcon })
        .bindPopup(`
          <div style="min-width: 200px;">
            <h3 style="margin: 0 0 8px 0; color: #10B981;"><strong>${currentSiteWithLocation.site_name}</strong></h3>
            <p style="margin: 4px 0;"><strong>Site Code:</strong> ${currentSiteWithLocation.site_code || 'N/A'}</p>
            <p style="margin: 4px 0;"><strong>Capacity:</strong> ${currentSiteWithLocation.site_capacity_mw || 'N/A'} MW</p>
            <p style="margin: 4px 0;"><strong>Type:</strong> ${currentSiteWithLocation.site_type || 'N/A'}</p>
            ${currentSiteWithLocation.commissioning_date ? `<p style="margin: 4px 0;"><strong>Commissioned:</strong> ${new Date(currentSiteWithLocation.commissioning_date).toLocaleDateString()}</p>` : ''}
            <p style="margin: 4px 0;"><strong>Location:</strong> ${lat.toFixed(4)}, ${lng.toFixed(4)}</p>
          </div>
        `)
        .addTo(leafletMap.current);

      // Zoom to site location with tighter zoom level
      leafletMap.current.setView([lat, lng], 18, {
        animate: true,
        duration: 1.0
      });
    }
  }, [currentSiteWithLocation]);

  // Update site marker when current site with location changes
  useEffect(() => {
    updateSiteMarker();
  }, [updateSiteMarker]);

  useEffect(() => {
    const geoJSONData = usingFallback ? fallbackGeoJSON : siteGeoJSON;
    if (!mapRef.current || leafletMap.current) return;

    // Initialize map
    leafletMap.current = L.map(mapRef.current, {
      // Performance optimizations
      preferCanvas: true, // Use canvas renderer for better performance
      zoomAnimation: true,
      fadeAnimation: true,
      markerZoomAnimation: true,
      // Set canvas renderer as default
      renderer: L.canvas({ padding: 0.5 })
    });

    // Set initial view - prioritize site location, then fit to entity bounds, then use default
    if (currentSiteWithLocation?.location && typeof currentSiteWithLocation.location === 'object' && currentSiteWithLocation.location.coordinates) {
      // Use site location if available
      const [lng, lat] = currentSiteWithLocation.location.coordinates;
      leafletMap.current.setView([lat, lng], 18);
    } else if (geoJSONData) {
      const bounds = L.geoJSON(geoJSONData as any).getBounds();
      if (bounds.isValid()) {
        leafletMap.current.fitBounds(bounds);
      } else {
        // Default view if bounds are invalid
        leafletMap.current.setView([33.5, -112.0], 18);
      }
    } else {
      // Default view for Arizona area if no data yet
      leafletMap.current.setView([33.5, -112.0], 18);
    }

    // Create optimized tile layer with error handling and multiple providers
    const createTileLayer = () => {
      const tileProviders = [
        {
          url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
          attribution: 'Tiles &copy; Esri',
          name: 'Esri Satellite'
        },
        {
          url: 'https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}',
          attribution: 'Tiles &copy; Google',
          name: 'Google Satellite'
        },
        {
          url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
          attribution: 'Tiles &copy; OpenStreetMap',
          name: 'OpenStreetMap'
        }
      ];

      let currentProviderIndex = 0;
      
      const tryNextProvider = () => {
        if (tileLayer.current) {
          leafletMap.current?.removeLayer(tileLayer.current);
        }
        
        const provider = tileProviders[currentProviderIndex];
        tileLayer.current = L.tileLayer(provider.url, {
          attribution: provider.attribution,
          maxZoom: 19,
          keepBuffer: 2, // Keep tiles in buffer for better performance
          updateWhenZooming: false, // Don't update during zoom for smoother experience
          updateWhenIdle: true, // Update when user stops moving
          crossOrigin: true
        });

        // Add error handling with fallback
        tileLayer.current.on('tileerror', (e: any) => {
          console.warn(`Tile error with ${provider.name}:`, e);
          currentProviderIndex++;
          if (currentProviderIndex < tileProviders.length) {
            setTimeout(tryNextProvider, 1000); // Try next provider after delay
          } else {
            console.error('All tile providers failed');
          }
        });

        tileLayer.current.addTo(leafletMap.current!);
      };

      tryNextProvider();
    };

    createTileLayer();

    // Add zoom event listener for scale-dependent rendering
    const handleZoomEnd = () => {
      if (leafletMap.current) {
        const zoom = leafletMap.current.getZoom();
        setCurrentZoom(zoom);
      }
    };

    leafletMap.current.on('zoomend', handleZoomEnd);

    // Set initial zoom
    setCurrentZoom(leafletMap.current.getZoom());

    // Now that the map is fully initialized, add markers
    updateSiteMarker();

    return () => {
      if (layerUpdateTimeout.current) {
        clearTimeout(layerUpdateTimeout.current);
      }
      if (siteMarker.current) {
        siteMarker.current.remove();
        siteMarker.current = null;
      }
      if (tileLayer.current) {
        tileLayer.current.remove();
        tileLayer.current = null;
      }
      if (leafletMap.current) {
        leafletMap.current.remove();
        leafletMap.current = null;
      }
      geoJsonLayers.current.clear();
    };
  }, [siteGeoJSON, fallbackGeoJSON, usingFallback, currentSiteWithLocation]);

  // Debounced layer update function
  const debouncedUpdateLayers = useCallback(() => {
    if (layerUpdateTimeout.current) {
      clearTimeout(layerUpdateTimeout.current);
    }
    
    layerUpdateTimeout.current = setTimeout(() => {
      const geoJSONData = usingFallback ? fallbackGeoJSON : siteGeoJSON;
      if (!leafletMap.current || !geoJSONData) return;

      // Clear existing layers efficiently
      geoJsonLayers.current.forEach((layer) => {
        leafletMap.current!.removeLayer(layer);
      });
      geoJsonLayers.current.clear();

      // Create layers for each layer type
      layerConfigs.forEach((layerConfig) => {
        if (!visibleLayers.has(layerConfig.id)) {
          return;
        }

        // Filter features for this layer type - no limiting, use zoom for performance
        const layerFeatures = (geoJSONData as any).features
          .filter((feature: any) => feature.properties.type === layerConfig.type);
        
        if (layerFeatures.length === 0) return;

        const layer = L.geoJSON(
          { type: 'FeatureCollection', features: layerFeatures } as any,
          {
            style: (feature) => {
              // Use the label property for selection checking
              const props = feature?.properties || {};
              const componentId = props.label || props.page_label || `${props.type}_${props.element_index}`;
              const isSelected = layerConfig.selectable && 
                state.selectedComponents.includes(componentId);
              
              // Enhanced styling for specific layer types
              const layerType = layerConfig.type;
              
              // For LineString geometries, don't use fill
              if (feature?.geometry?.type === 'LineString') {
                return {
                  weight: isSelected ? 3 : 2,
                  opacity: 0.7,
                  color: isSelected ? '#fbbf24' : layerConfig.color,
                };
              }
              
              // Special styling for combiner boxes and rack outlines
              if (layerType === 'combiner_boxes' || layerType === 'combiner_outlines') {
                return {
                  fillColor: layerConfig.color,
                  weight: isSelected ? 4 : 2,
                  opacity: 0.9, // Strong outline
                  color: isSelected ? '#fbbf24' : '#10B981', // Bright green outline
                  fillOpacity: isSelected ? 0.7 : 0.3
                };
              }
              
              if (layerType === 'rack_outlines') {
                return {
                  fillColor: layerConfig.color,
                  weight: isSelected ? 2 : 1, // Skinny white lines
                  opacity: 0.9, // Strong outline
                  color: isSelected ? '#fbbf24' : '#FFFFFF', // White outline for better visibility
                  fillOpacity: isSelected ? 0.7 : 0.3
                };
              }
              
              // Special styling for inverters
              if (layerType.includes('inverter')) {
                return {
                  fillColor: layerConfig.color,
                  weight: isSelected ? 4 : 2,
                  opacity: 0.9, // Strong outline
                  color: isSelected ? '#fbbf24' : '#87CEEB', // Light blue outline
                  fillOpacity: isSelected ? 0.7 : 0.3
                };
              }
              
              // Default styling for other layers
              return {
                fillColor: layerConfig.color,
                weight: isSelected ? 3 : 1,
                opacity: 0.6,
                color: isSelected ? '#fbbf24' : layerConfig.color,
                fillOpacity: isSelected ? 0.6 : 0.2
              };
            },
            onEachFeature: (feature, layer) => {
              const props = feature.properties;
              
              // Simplified popup content for better performance
              const popupContent = `
                <div class="p-2 bg-engramiq-dark-blue text-engramiq-light-grey">
                  <h3 class="font-bold text-engramiq-green">${layerConfig.name}</h3>
                  <p><strong>Type:</strong> ${props.type || 'Unknown'}</p>
                  ${props.label ? `<p><strong>ID:</strong> ${props.label}</p>` : ''}
                  ${props.page_label && props.page_label !== props.label ? `<p><strong>Page Label:</strong> ${props.page_label}</p>` : ''}
                </div>
              `;
              layer.bindPopup(popupContent);

              // Add click handler only for selectable layers
              if (layerConfig.selectable) {
                layer.on('click', (e) => {
                  e.originalEvent?.stopPropagation();
                  const currentSelection = state.selectedComponents;
                  // Use the label property for the component ID
                  const componentId = props.label || props.page_label || `${props.type}_${props.element_index}`;
                  
                  if (currentSelection.includes(componentId)) {
                    setSelectedComponents(currentSelection.filter(id => id !== componentId));
                  } else {
                    setSelectedComponents([...currentSelection, componentId]);
                  }
                });
              }
            }
          }
        );

        if (leafletMap.current) {
          layer.addTo(leafletMap.current);
          geoJsonLayers.current.set(layerConfig.id, layer);
        }
      });
    }, DEBOUNCE_DELAY);
  }, [state.selectedComponents, setSelectedComponents, visibleLayers, layerConfigs, usingFallback, fallbackGeoJSON, siteGeoJSON]);

  // Update layers when visibility or selection changes
  useEffect(() => {
    debouncedUpdateLayers();
  }, [debouncedUpdateLayers]);

  // Toggle layer visibility
  const toggleLayerVisibility = (layerId: string) => {
    setVisibleLayers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(layerId)) {
        newSet.delete(layerId);
      } else {
        newSet.add(layerId);
      }
      return newSet;
    });
  };

  // Show loading state
  if (loading || loadingGeoJSON) {
    const progressPercent = totalChunks > 0 ? (processedChunks / totalChunks) * 100 : 0;
    
    return (
      <div className="w-full h-full flex items-center justify-center bg-engramiq-dark-blue">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-engramiq-green border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <div className="text-engramiq-light-grey font-medium mb-2">
            {loading ? 'Loading...' : 'Processing GeoJSON data...'}
          </div>
          {loadingGeoJSON && totalChunks > 0 && (
            <>
              <div className="w-64 bg-engramiq-bright-grey/20 rounded-full h-2 mb-2 mx-auto">
                <div 
                  className="bg-engramiq-green h-2 rounded-full transition-all duration-300 ease-out" 
                  style={{ width: `${progressPercent}%` }}
                ></div>
              </div>
              <div className="text-engramiq-bright-grey text-sm">
                {processedChunks} / {totalChunks} chunks processed ({Math.round(progressPercent)}%)
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // Don't show no data state - always render the map container
  // The map will either show database data, fallback data, or be empty
  // This ensures the map initializes properly

  return (
    <div className="w-full h-full relative">
      <div ref={mapRef} className="w-full h-full" />
      

      {/* Layer Controls - Hidden */}
    </div>
  );
};

export default MapTab;